import { useOktaAuth } from "@okta/okta-react";
import { createContext, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { SavedAddress } from "../../api/orders/orderInterfaces";
import {
  SanitizedAddressResponse,
  ValidationReport,
} from "../../api/validation/validationInterfaces";
import useXtremeBffApi from "../../api/xtremeBff/useXtremeBff";
import xtremeBffApi from "../../api/xtremeBff/xtremeBffApi";
import { CATEGORIES, TOGGLES } from "../../pages/featureToggles/constants";
import {
  Address,
  getSapOrderType,
  urlParamsToOrderTypeMapping,
} from "../../pages/orders/types";
import { isToggleEnabledForOrderType } from "../../pages/orders/utils";
import { ConfigContext } from "../ConfigContext";
import { DealerAccountContext } from "../DealerAccountContext";
import { FeatureToggleContextV2 } from "../FeatureToggleContextV2/FeatureToggleContextV2";

export interface OrderContextProps {
  setShipToAddressValidationReport: (
    validationReport: ValidationReport
  ) => void;
  shipToAddressValidationReport: ValidationReport;
  setV2AddressValidationReport: (
    validationReport: SanitizedAddressResponse
  ) => void;
  v2AddressValidationReport: SanitizedAddressResponse;
  getShipToAddressList: () => SavedAddress[];
  setDefaultShipToAddress: (address: Address | undefined) => void;
  getDefaultShipToAddress: () => Address | undefined;
}

export const OrderContext = createContext<OrderContextProps>({
  setShipToAddressValidationReport: () => {},
  shipToAddressValidationReport: {} as ValidationReport,
  setV2AddressValidationReport: () => {},
  v2AddressValidationReport: {} as SanitizedAddressResponse,
  getShipToAddressList: () => [],
  setDefaultShipToAddress: () => {},
  getDefaultShipToAddress: () => undefined,
});

export const OrderContextProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { dealerAccount } = useContext(DealerAccountContext);
  const { orderType } = useParams<{ orderType?: string }>();
  const {
    XTREME_BFF_SERVICE_URL,
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE,
    ENABLE_ORDER_TYPE_FILTER_FOR_SHIPPING_ADDRESSES,
  } = useContext(ConfigContext);
  const { isToggleEnabled, additionalFieldsValues } = useContext(
    FeatureToggleContextV2
  );
  const { authState } = useOktaAuth();
  const [shipToAddressValidationReport, setShipToAddressValidationReport] =
    useState<ValidationReport>({
      status: "SUCCESS",
      validations: [],
    });
  const [v2AddressValidationReport, setV2AddressValidationReport] =
    useState<SanitizedAddressResponse>({
      status: "SUCCESS",
      suggestedValidAddresses: [],
    });
  const [shipToAddressList, setShipToAddressList] = useState<SavedAddress[]>(
    []
  );
  const [defaultShipToAddress, setDefaultShipToAddress] = useState<Address>();

  const isDealerServiceAuthEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE;
  const bffApiHook = useXtremeBffApi();

  const getOrderTypeKey = (urlOrderType?: string): string | undefined => {
    if (!urlOrderType) return undefined;
    return (
      urlParamsToOrderTypeMapping[urlOrderType] ?? urlOrderType
    ).toLowerCase();
  };

  const isAddressSelectionEnabledForOrderType = (): boolean =>
    isToggleEnabledForOrderType(
      isToggleEnabled,
      additionalFieldsValues,
      CATEGORIES.ORDER_SERVICE,
      TOGGLES.ENABLE_ADDRESS_OVERRIDE_ADDRESS_SELECTION,
      getOrderTypeKey(orderType)
    );

  useEffect(() => {
    if (
      ENABLE_FEATURE_TOGGLE_CONTEXT_V2 &&
      isAddressSelectionEnabledForOrderType()
    ) {
      const sapOrderType = ENABLE_ORDER_TYPE_FILTER_FOR_SHIPPING_ADDRESSES
        ? getSapOrderType(orderType)
        : undefined;

      if (isDealerServiceAuthEnabled) {
        const req = ENABLE_ORDER_TYPE_FILTER_FOR_SHIPPING_ADDRESSES
          ? bffApiHook.getShippingAddresses(
              XTREME_BFF_SERVICE_URL,
              dealerAccount?.id as string,
              sapOrderType
            )
          : bffApiHook.getShippingAddresses(
              XTREME_BFF_SERVICE_URL,
              dealerAccount?.id as string
            );
        req.then((shippingAddresses) =>
          setShipToAddressList(shippingAddresses)
        );
      } else {
        const req = ENABLE_ORDER_TYPE_FILTER_FOR_SHIPPING_ADDRESSES
          ? xtremeBffApi.getShippingAddresses(
              XTREME_BFF_SERVICE_URL,
              authState?.accessToken?.accessToken as string,
              dealerAccount?.id as string,
              sapOrderType
            )
          : xtremeBffApi.getShippingAddresses(
              XTREME_BFF_SERVICE_URL,
              authState?.accessToken?.accessToken as string,
              dealerAccount?.id as string
            );
        req.then((shippingAddresses) =>
          setShipToAddressList(shippingAddresses)
        );
      }
    }
  }, [dealerAccount?.id, orderType]);

  const getShipToAddressList = () => shipToAddressList;

  const getDefaultShipToAddress = () => defaultShipToAddress;

  return (
    <OrderContext.Provider
      value={{
        setShipToAddressValidationReport,
        shipToAddressValidationReport,
        setV2AddressValidationReport,
        v2AddressValidationReport,
        getShipToAddressList,
        setDefaultShipToAddress,
        getDefaultShipToAddress,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};
